import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { SelectorItem } from '@app/component/selector/selector.component';
import { Market } from '@app/model/payment-request/payment-request.dto';
import { RouteComponent } from '@app/route/route.component';
import { LoanOptionVM } from '@app/route/shared/LoanOptionVM';
import { HeaderService } from '@app/service/header/header.service';
import { NoUserService } from '@app/service/no-user-service/no-user.service';
import { StateService } from '@app/service/state/state.service';
import { ValuePipe } from '@app/shared/pipe/value.pipe';

@Component({
  selector: 'no-installments-selection',
  templateUrl: './no-installments-selection.component.html',
  styleUrls: [
    '../../page-shared.css',
    './no-installments-selection.component.scss',
  ],
})
export class NoInstallmentsSelectionComponent
  extends RouteComponent
  implements OnInit
{
  availableLoanOptions: LoanOptionVM[] = null;
  presentedLoanOptions: LoanOptionVM[] = null;
  selectedLoanOption: LoanOptionVM = null;
  expanded: boolean = false;
  equalOrLessThanThreeLoanOptions: boolean = false;
  navigationInProgress: boolean = false;
  currentLoanTermsStr: string = '';
  Market = Market;
  selectorItems: SelectorItem[] = null;
  modalActive: boolean = false;
  loading: boolean = false;

  preferredInstallmentPlanDurations: number[] = null; // [6, 12, 48];

  constructor(
    protected router: Router,
    protected route: ActivatedRoute,
    protected stateService: StateService,
    protected headerService: HeaderService,
    private noUserService: NoUserService,
    private valuePipe: ValuePipe
  ) {
    super(router, route, stateService, headerService);
  }

  ngOnInit(): void {
    this.loading = true;
    this.availableLoanOptions = this.noUserService.transformDtosTOVMs(
      this.state.loanOptions.sort((a, b) => a.monthlyCost - b.monthlyCost),
      this.toggleModal.bind(this)
    );

    this.equalOrLessThanThreeLoanOptions =
      this.availableLoanOptions.length <= 3;

    this.getPresentedLoanOptions();
    this.selectedLoanOption = this.presentedLoanOptions[0];
    this.selectInstallment(this.selectedLoanOption);
    this.loading = false;
  }

  async handleCtaClick(): Promise<void> {
    try {
      await this.noUserService.selectInstallmentPlan(
        this.selectedLoanOption.termId
      );
      this.navigate();
    } catch (error) {
      console.log(error);
    }
  }

  selectInstallment(loanOption: LoanOptionVM): void {
    this.selectedLoanOption = loanOption;
    this.currentLoanTermsStr = this.noUserService.buildLoanTermsString(
      this.selectedLoanOption,
      this.state.totalCheckoutValue
    );
    this.selectorItems = [
      {
        tab: 'Beskrivelse',
        title: 'Sammendrag',
        lineItems: this.state.paymentInformation?.lineItems,
        totalValue: `Totalbeløp inkl. mva: ${this.valuePipe.transform(
          this.state.totalCheckoutValue
        )}`,
      },
      {
        tab: 'Betalingsbetingelser',
        title: 'Vilkår for delbetaling',
        descriptions: [{ text: this.currentLoanTermsStr }],
        ...(!!loanOption.loanTermsURL && {
          links: [
            {
              text: 'Kredittavtale',
              onClick: () => this.openURLinNewWindow(loanOption.loanTermsURL),
            },
            {
              text: 'Standardiserte europeiske opplysninger om Forbrukerkreditt (SEF-skjema)',
              onClick: () => this.openURLinNewWindow(loanOption.secciURL),
            },
          ],
        }),
      },
    ];
  }

  openURLinNewWindow(url: string): void {
    window.open(url, '_blank');
  }

  getPresentedLoanOptions(): void {
    if (this.equalOrLessThanThreeLoanOptions) {
      this.presentedLoanOptions = this.availableLoanOptions;
      return;
    }

    const middleMostIndex: number = Math.floor(
      this.availableLoanOptions.length / 2
    );

    const longest: LoanOptionVM = this.availableLoanOptions[0];
    const middlemost: LoanOptionVM = this.availableLoanOptions[middleMostIndex];
    const shortest: LoanOptionVM =
      this.availableLoanOptions[this.availableLoanOptions.length - 1];

    this.presentedLoanOptions = [longest, middlemost, shortest];

    if (this.preferredInstallmentPlanDurations?.length > 0) {
      this.presentedLoanOptions = this.availableLoanOptions.filter(
        (loanOption: LoanOptionVM) =>
          this.preferredInstallmentPlanDurations.includes(
            loanOption.numberOfMonths
          )
      );
    }

    // if we have selected installment, replace closest
    if (this.selectedLoanOption) {
      const closestLoanOption: LoanOptionVM = this.getClosestLoanOption(
        this.presentedLoanOptions,
        this.selectedLoanOption.monthlyCost
      );

      // replace closest installment with selected loan option
      this.presentedLoanOptions = this.presentedLoanOptions.map(
        (loanOption: LoanOptionVM) =>
          loanOption.monthlyCost === closestLoanOption.monthlyCost
            ? this.selectedLoanOption
            : loanOption
      );
    }
  }

  toggleModal(): void {
    this.modalActive = !this.modalActive;
  }

  viewAllLoanOptions(): void {
    this.expanded = true;
    this.presentedLoanOptions = this.availableLoanOptions;
  }

  viewReducedLoanOptions(): void {
    this.expanded = false;
    this.getPresentedLoanOptions();
  }

  getClosestLoanOption(
    loanOption: LoanOptionVM[],
    selectedLoanOptionMonthlyCost: number
  ): LoanOptionVM {
    const monthlyCostDiff = (monthlyCost: number) =>
      Math.abs(monthlyCost - selectedLoanOptionMonthlyCost);
    return loanOption.reduce((prev: LoanOptionVM, curr: LoanOptionVM) =>
      monthlyCostDiff(curr.monthlyCost) < monthlyCostDiff(prev.monthlyCost)
        ? curr
        : prev
    );
  }
}
